import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { Spinner } from '@/components/ui/spinner';
import { ApplicationReturnLink } from '@/components/ApplicationReturnLink';
import { 
  KeyRound, 
  Shield, 
  CheckCircle2, 
  Info,
  Fingerprint,
  Usb,
  AlertCircle,
  Key,
  Wifi,
  Bluetooth,
  Smartphone
} from 'lucide-react';

interface Step {
  id: number;
  title: string;
  description: string;
  icon: React.ReactNode;
  status: 'waiting' | 'active' | 'completed' | 'error';
}

export default function PasskeySetup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [showTouchIndicator, setShowTouchIndicator] = useState(false);
  const [applicationName, setApplicationName] = useState<string | null>(null);
  const [allowedTransports, setAllowedTransports] = useState<string[]>([]);
  const [steps, setSteps] = useState<Step[]>([
    {
      id: 1,
      title: 'Connect Key',
      description: 'Insert your USB security key or have your NFC/Bluetooth key ready',
      icon: <Usb className="h-5 w-5" />,
      status: 'waiting'
    },
    {
      id: 2,
      title: 'Create Passkey',
      description: 'Your browser will prompt you to create a new passkey',
      icon: <KeyRound className="h-5 w-5" />,
      status: 'waiting'
    },
    {
      id: 3,
      title: 'Verify',
      description: 'Physically touch your security key to confirm',
      icon: <Fingerprint className="h-5 w-5" />,
      status: 'waiting'
    },
    {
      id: 4,
      title: 'Complete',
      description: 'Passkey will be registered with our server',
      icon: <CheckCircle2 className="h-5 w-5" />,
      status: 'waiting'
    }
  ]);
  const [currentStep, setCurrentStep] = useState(0);

  const userId = searchParams.get('user');

  useEffect(() => {
    if (!userId) {
      toast({
        title: "Invalid request",
        description: "Please log in again",
        variant: "destructive",
      });
      setTimeout(() => navigate('/'), 2000);
    }
    
    // Load application name from sessionStorage
    const storedAppName = sessionStorage.getItem('application_name');
    if (storedAppName) {
      setApplicationName(storedAppName);
    }
    
    // Load allowed transports from sessionStorage
    const storedTransports = sessionStorage.getItem('allowed_transports');
    if (storedTransports) {
      try {
        const transports = JSON.parse(storedTransports);
        setAllowedTransports(transports);
      } catch (e) {
        console.error('Failed to parse allowed transports:', e);
      }
    }
  }, [userId, navigate, toast]);

  const updateStepStatus = (stepId: number, status: Step['status']) => {
    setSteps(prev => prev.map(step => 
      step.id === stepId ? { ...step, status } : step
    ));
  };

  const base64ToArrayBuffer = (base64: string): ArrayBuffer => {
    const binaryString = window.atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  };

  const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  };

  const arrayBufferToBase64url = (buffer: ArrayBuffer): string => {
    const base64 = arrayBufferToBase64(buffer);
    return base64
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  };

  const setupPasskey = async () => {
    setLoading(true);
    setCurrentStep(1);
    updateStepStatus(1, 'active');

    try {
      // Check if WebAuthn is supported
      if (!window.PublicKeyCredential) {
        throw new Error('WebAuthn is not supported in this browser');
      }

      // Get Google ID token from session storage
      const googleIdToken = sessionStorage.getItem('google_id_token');
      if (!googleIdToken) {
        throw new Error('Authentication required. Please sign in again.');
      }
      
      // Get session ID if available
      const sessionId = sessionStorage.getItem('session_id');

      // Step 1: Get registration challenge
      const challengeResponse = await fetch('https://identity.tools.agentic.inv.tech/passkey/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          action: 'begin',
          userId,
          id_token: googleIdToken,
          session_id: sessionId
        })
      });

      const challengeData = await challengeResponse.json();
      
      if (!challengeResponse.ok) {
        throw new Error(challengeData.error_description || challengeData.error || 'Failed to get registration challenge');
      }

      const credentialCreationOptions = challengeData;
      
      // Update allowed transports if provided by server
      if (challengeData.allowed_transports) {
        setAllowedTransports(challengeData.allowed_transports);
        // Also update sessionStorage for consistency
        sessionStorage.setItem('allowed_transports', JSON.stringify(challengeData.allowed_transports));
      }

      // Convert base64 strings to ArrayBuffers
      credentialCreationOptions.publicKey.challenge = base64ToArrayBuffer(credentialCreationOptions.publicKey.challenge);
      credentialCreationOptions.publicKey.user.id = base64ToArrayBuffer(credentialCreationOptions.publicKey.user.id);


      updateStepStatus(1, 'completed');
      setCurrentStep(2);
      updateStepStatus(2, 'active');

      // Step 2: Create credential
      const credential = await navigator.credentials.create(credentialCreationOptions) as PublicKeyCredential;
      
      updateStepStatus(2, 'completed');
      setCurrentStep(3);
      updateStepStatus(3, 'active');
      setShowTouchIndicator(true);

      // Verify it's using allowed transports
      const response = credential.response as AuthenticatorAttestationResponse;
      const transports = response.getTransports ? response.getTransports() : [];
      
      console.log('Authenticator transports:', transports);
      console.log('Allowed transports:', allowedTransports);
      
      // Check if the authenticator uses at least one allowed transport
      if (allowedTransports.length > 0) {
        const hasAllowedTransport = transports.some(transport => allowedTransports.includes(transport));
        if (!hasAllowedTransport) {
          const allowedTypes = allowedTransports.map(t => {
            switch(t) {
              case 'usb': return 'USB security key';
              case 'nfc': return 'NFC security key';
              case 'ble': return 'Bluetooth security key';
              case 'internal': return 'platform authenticator (Touch ID, Face ID, Windows Hello)';
              case 'hybrid': return 'phone as security key';
              default: return t;
            }
          }).join(', ');
          throw new Error(`This application only allows: ${allowedTypes}. Your authenticator type is not supported.`);
        }
      } else {
        // Fallback validation if no specific transports are configured
        if (!transports.includes('usb') && !transports.includes('nfc') && !transports.includes('ble') && !transports.includes('internal')) {
          throw new Error('Only hardware security keys and platform authenticators are allowed. Password managers are not supported.');
        }
      }

      setShowTouchIndicator(false);
      updateStepStatus(3, 'completed');
      setCurrentStep(4);
      updateStepStatus(4, 'active');

      // Step 4: Register with server
      const credentialIdBase64url = arrayBufferToBase64url(credential.rawId);
      const registrationData = {
        action: 'complete',
        userId,
        credentialId: credentialIdBase64url,
        clientDataJSON: arrayBufferToBase64(response.clientDataJSON),
        attestationObject: arrayBufferToBase64(response.attestationObject),
        transports: transports,
        id_token: googleIdToken,
        session_id: sessionId
      };

      const verificationResponse = await fetch('https://identity.tools.agentic.inv.tech/passkey/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(registrationData)
      });

      const result = await verificationResponse.json();
      
      if (!verificationResponse.ok) {
        throw new Error(result.error_description || result.error || 'Failed to verify security key');
      }

      if (result.success) {
        updateStepStatus(4, 'completed');
        
        // Store user ID 
        localStorage.setItem('user_id', userId || '');
        
        // Update user object with new credentials
        if (result.credentials && result.credentials.length > 0) {
          const storedUser = localStorage.getItem('user');
          if (storedUser) {
            try {
              const user = JSON.parse(storedUser);
              user.credentials = result.credentials;
              localStorage.setItem('user', JSON.stringify(user));
            } catch (error) {
              console.error('Failed to update user credentials:', error);
            }
          }
        }

        toast({
          title: "Success!",
          description: "Your security key has been registered. Redirecting...",
        });

        // Redirect
        setTimeout(() => {
          window.location.href = '/client/passkey/verify';
        }, 1500);
      } else {
        throw new Error(result.error || 'Registration failed');
      }

    } catch (err: unknown) {
      console.error('Passkey setup error:', err);
      
      // Update step status to show error
      if (currentStep > 0) {
        updateStepStatus(currentStep, 'error');
      }

      const errorTitle = 'Setup failed';
      let errorDescription = 'Please try again with a hardware security key.';
      
      if (err instanceof Error) {
        if (err.name === 'NotAllowedError') {
          errorDescription = 'The operation was cancelled or timed out. Make sure you are using a physical security key.';
        } else if (err.name === 'SecurityError') {
          errorDescription = 'This origin is not allowed to use WebAuthn.';
        } else if (err.name === 'NotSupportedError') {
          errorDescription = 'Your security key is not supported. Please use a FIDO2-compatible hardware key.';
          // Reset to beginning when wrong transport type is used
          setCurrentStep(0);
          // Reset all step statuses
          setSteps(prev => prev.map(step => ({ ...step, status: 'waiting' })));
        } else if (err.message.includes('platform authenticators')) {
          errorDescription = err.message;
        } else if (err.message.includes('This application only allows')) {
          // Reset to beginning when wrong transport type is used
          errorDescription = err.message;
          setCurrentStep(0);
          // Reset all step statuses
          setSteps(prev => prev.map(step => ({ ...step, status: 'waiting' })));
        } else {
          errorDescription = err.message;
        }
      }

      toast({
        title: errorTitle,
        description: errorDescription,
        variant: "destructive",
      });
      
      setShowTouchIndicator(false);
    } finally {
      setLoading(false);
    }
  };

  const skipSetup = () => {
    // Get the stored application URL
    const applicationUrl = sessionStorage.getItem('application_url');
    if (applicationUrl) {
      // Redirect back to the application
      window.location.href = applicationUrl;
    } else {
      // Navigate back to login page if no application URL
      navigate('/');
    }
  };

  const activeStep = steps.find(s => s.status === 'active');

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="bg-white dark:bg-gray-950 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white">
                <Shield className="h-6 w-6" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold tracking-tight">Set up your security key</h1>
                <p className="text-sm text-muted-foreground">
                  {applicationName 
                    ? `Secure your access to ${applicationName}` 
                    : 'Enhance your account security with a hardware security key'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
        {/* What You'll Need Box and Action Button */}
        <div className="space-y-6 mb-8">
          {/* What You'll Need Box */}
          <Card className="bg-amber-50 dark:bg-amber-950 border-amber-200 dark:border-amber-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Info className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-100">
                  What you'll need
                </h3>
              </div>
              
              {allowedTransports.length > 0 ? (
                <div>
                  <p className="text-xs text-amber-700 dark:text-amber-300 mb-3">
                    This application supports the following security key types:
                  </p>
                  <div className="flex justify-around items-center">
                    {allowedTransports.includes('usb') && (
                      <div className="flex flex-col items-center text-center flex-1">
                        <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900 rounded-full flex items-center justify-center mb-1">
                          <Usb className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                        </div>
                        <p className="font-medium text-xs text-amber-900 dark:text-amber-100">USB Key</p>
                        <p className="text-[10px] text-amber-700 dark:text-amber-300 mt-0.5">
                          Physical USB
                        </p>
                      </div>
                    )}
                    {allowedTransports.includes('nfc') && (
                      <div className="flex flex-col items-center text-center flex-1">
                        <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900 rounded-full flex items-center justify-center mb-1">
                          <Wifi className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                        </div>
                        <p className="font-medium text-xs text-amber-900 dark:text-amber-100">NFC Key</p>
                        <p className="text-[10px] text-amber-700 dark:text-amber-300 mt-0.5">
                          Tap to connect
                        </p>
                      </div>
                    )}
                    {allowedTransports.includes('ble') && (
                      <div className="flex flex-col items-center text-center flex-1">
                        <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900 rounded-full flex items-center justify-center mb-1">
                          <Bluetooth className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                        </div>
                        <p className="font-medium text-xs text-amber-900 dark:text-amber-100">Bluetooth</p>
                        <p className="text-[10px] text-amber-700 dark:text-amber-300 mt-0.5">
                          Wireless key
                        </p>
                      </div>
                    )}
                    {allowedTransports.includes('internal') && (
                      <div className="flex flex-col items-center text-center flex-1">
                        <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900 rounded-full flex items-center justify-center mb-1">
                          <Fingerprint className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                        </div>
                        <p className="font-medium text-xs text-amber-900 dark:text-amber-100">Biometric</p>
                        <p className="text-[10px] text-amber-700 dark:text-amber-300 mt-0.5">
                          Touch/Face ID
                        </p>
                      </div>
                    )}
                    {allowedTransports.includes('hybrid') && (
                      <div className="flex flex-col items-center text-center flex-1">
                        <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900 rounded-full flex items-center justify-center mb-1">
                          <Smartphone className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                        </div>
                        <p className="font-medium text-xs text-amber-900 dark:text-amber-100">Phone</p>
                        <p className="text-[10px] text-amber-700 dark:text-amber-300 mt-0.5">
                          QR code
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                // Fallback if no allowed transports are specified
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-amber-100 dark:bg-amber-900 rounded flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Key className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                      <p className="font-medium text-sm text-amber-900 dark:text-amber-100">Security Key</p>
                      <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                        FIDO2-compatible hardware key (YubiKey, Titan Key, etc.)
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-amber-100 dark:bg-amber-900 rounded flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Usb className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                      <p className="font-medium text-sm text-amber-900 dark:text-amber-100">Connection</p>
                      <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                        USB port or NFC capability
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-amber-100 dark:bg-amber-900 rounded flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Fingerprint className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                      <p className="font-medium text-sm text-amber-900 dark:text-amber-100">Touch</p>
                      <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                        Physical touch when prompted
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>


          {/* Action Buttons */}
          <div className="flex gap-4 justify-center">
            <Button 
              onClick={setupPasskey} 
              size="lg"
              disabled={loading}
              className="min-w-[200px]"
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Setting up security key...
                </>
              ) : (
                <>
                  <KeyRound className="mr-2 h-5 w-5" />
                  Set up security key
                </>
              )}
            </Button>

            <Button
              variant="outline"
              onClick={skipSetup}
              disabled={loading}
            >
              I'll set this up later
            </Button>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="bg-gray-50 dark:bg-gray-900 border rounded-lg p-8 mb-8">
          <div className="flex items-center justify-between relative">
            {/* Progress Line */}
            <div className="absolute left-0 right-0 top-5 h-0.5 bg-gray-200 dark:bg-gray-700">
              <div 
                className="h-full bg-blue-600 transition-all duration-500"
                style={{ width: `${currentStep === 0 ? 0 : ((currentStep - 1) / (steps.length - 1)) * 100}%` }}
              />
            </div>

            {/* Steps */}
            {steps.map((step) => (
              <div 
                key={step.id} 
                className="relative z-10 flex flex-col items-center gap-2 flex-1"
              >
                {/* Step Circle */}
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                    step.status === 'completed' ? 'bg-green-600 text-white' :
                    step.status === 'active' ? 'bg-blue-600 text-white ring-4 ring-blue-100 dark:ring-blue-900' :
                    step.status === 'error' ? 'bg-red-600 text-white' :
                    'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                  }`}
                >
                  {step.status === 'completed' ? (
                    <CheckCircle2 className="h-5 w-5" />
                  ) : step.status === 'active' ? (
                    <Spinner size="sm" className="text-white" />
                  ) : step.status === 'error' ? (
                    <AlertCircle className="h-5 w-5" />
                  ) : (
                    <span className="text-sm font-medium">{step.id}</span>
                  )}
                </div>

                {/* Step Title */}
                <span className={`text-sm font-medium ${
                  step.status === 'active' || step.status === 'completed' 
                    ? 'text-gray-900 dark:text-gray-100' 
                    : 'text-gray-500 dark:text-gray-400'
                }`}>
                  {step.title}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Active Step Details */}
        {activeStep && (
          <Card className="border-2 border-blue-200 dark:border-blue-800">
            <CardContent className="p-8">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center flex-shrink-0">
                  {activeStep.icon}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold mb-2">{activeStep.title}</h3>
                  <p className="text-muted-foreground">{activeStep.description}</p>
                </div>
              </div>

              {showTouchIndicator && (
                <Alert className="mt-6 border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950">
                  <Fingerprint className="h-4 w-4 text-blue-600 dark:text-blue-400 animate-pulse" />
                  <AlertTitle className="text-blue-900 dark:text-blue-100">
                    Touch your security key now
                  </AlertTitle>
                  <AlertDescription className="text-blue-800 dark:text-blue-200">
                    Your browser is waiting for you to physically touch your security key to confirm.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        )}
      </div>
      
      {/* Application return link */}
      <ApplicationReturnLink className="mt-8 pb-8" />
    </div>
  );
}